/**
 * @fileoverview Hook to fetch linked invoices for a payment.
 * @module features/payments/hooks/usePaymentLinkedInvoices
 */

import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import apiClient from '@/lib/apiClient'

const linkedInvoiceSchema = z.object({
  invoiceUuid: z.string().uuid(),
  invoiceNumber: z.string(),
  counterparty: z.string(),
  saleDate: z.string(),
  issueDate: z.string().nullable().optional(),
  netAmount: z.number(),
  grossAmount: z.number(),
  currency: z.string(),
  linkedAt: z.string(),
  hasFile: z.boolean(),
})

const linkedInvoicesSchema = z.array(linkedInvoiceSchema).default([])

export type LinkedInvoice = z.infer<typeof linkedInvoiceSchema>

async function fetchLinkedInvoices(paymentId: string): Promise<LinkedInvoice[]> {
  const response = await apiClient.get(`/universal-transactions/${paymentId}/invoice-links`)
  return linkedInvoicesSchema.parse(response.data)
}

export function usePaymentLinkedInvoices(
  paymentId: string | null,
  options?: { enabled?: boolean },
): ReturnType<typeof useQuery<LinkedInvoice[], Error>> {
  return useQuery<LinkedInvoice[], Error>({
    queryKey: ['payments', 'linked-invoices', paymentId],
    queryFn: () => fetchLinkedInvoices(paymentId!),
    enabled: Boolean(paymentId) && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  })
}

